import { Button, DialogContentText } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/styles";
import ClusterListPage from "@saleor/cluster/components/ClusterListPage";
import { TypedClusterBulkDelete } from "@saleor/cluster/mutations";
import { useClusterListQuery } from "@saleor/cluster/queries";
import { ClusterBulkDelete } from "@saleor/cluster/types/ClusterBulkDelete";
import {
  clusterAddUrl,
  clusterListUrl,
  ClusterListUrlDialog,
  ClusterListUrlQueryParams,
  clusterUrl
} from "@saleor/cluster/urls";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
// import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
// import { getArrayQueryParam } from "@saleor/utils/urls";
import React from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filter";

interface ClusterListProps {
  params: ClusterListUrlQueryParams;
}

const useStyles = makeStyles(
  {
    deleteIcon: {
      marginRight: "8px"
    }
  },
  { name: "ClusterList" }
);

export const ClusterList: React.FC<ClusterListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();
  const classes = useStyles("");
  const intl = useIntl();

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const currencySymbol = shop?.defaultCurrency || "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.CLUSTER_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const { data, loading, refetch } = useClusterListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables
    }
  });

  React.useEffect(
    () =>
      navigate(
        clusterListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: clusterListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ClusterListUrlDialog,
    ClusterListUrlQueryParams
  >(navigate, clusterListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      clusterListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(clusterListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.clusters.pageInfo),
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(params);

  const handleClusterBulkDelete = (data: ClusterBulkDelete) => {
    if (data.clusterBulkDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Clusters successfully deleted."
        })
      });
      reset();
      closeModal();
      refetch();
    } else {
      notify({
        status: "error",
        text: `${data.clusterBulkDelete.errors[0].message}`
      });
    }
  };

  return (
    <TypedClusterBulkDelete onCompleted={handleClusterBulkDelete}>
      {(clusterBulkDelete, clusterBulkDeleteOpts) => {
        const onClusterBulkDelete = () =>
          clusterBulkDelete({
            variables: {
              ids: params.ids
            }
          });

        return (
          <>
            <ClusterListPage
              brands={null}
              clusters={maybe(
                () => data.clusters.edges.map(edge => edge.node),
                []
              )}
              currencySymbol={currencySymbol}
              currentTab={currentTab}
              filterOpts={filterOpts}
              initialSearch={params.query || ""}
              onSearchChange={handleSearchChange}
              onFilterChange={changeFilters}
              onAll={resetFilters}
              onTabChange={handleTabChange}
              onTabDelete={() => openModal("delete-search")}
              onTabSave={() => openModal("save-search")}
              tabs={tabs.map(tab => tab.name)}
              settings={settings}
              onAdd={() => navigate(clusterAddUrl())}
              onRowClick={id => () => navigate(clusterUrl(id))}
              disabled={loading || clusterBulkDeleteOpts.loading}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              onUpdateListSettings={updateListSettings}
              pageInfo={pageInfo}
              isChecked={isSelected}
              toggle={toggle}
              toggleAll={toggleAll}
              selected={listElements.length}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("delete", {
                      ids: listElements
                    })
                  }
                >
                  <DeleteIcon className={classes.deleteIcon} />
                </Button>
              }
            />
            <ActionDialog
              confirmButtonState={clusterBulkDeleteOpts.status}
              onClose={closeModal}
              onConfirm={onClusterBulkDelete}
              open={params.action === "delete"}
              title={intl.formatMessage({
                defaultMessage: "Delete clusters",
                description: "dialog header"
              })}
              confirmButtonLabel="Delete"
              variant="delete"
            >
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="{counter,plural,one{Are you sure you want to delete this cluster?} other{Are you sure you want to delete {displayQuantity} clusters?}}"
                  description="dialog content"
                  values={{
                    counter: params && params.ids && params.ids.length,
                    displayQuantity: (
                      <strong>
                        {params && params.ids && params.ids.length}
                      </strong>
                    )
                  }}
                />
              </DialogContentText>
            </ActionDialog>
            <SaveFilterTabDialog
              open={params.action === "save-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabSave}
            />
            <DeleteFilterTabDialog
              open={params.action === "delete-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabDelete}
              tabName={maybe(() => tabs[currentTab - 1].name, "...")}
            />
          </>
        );
      }}
    </TypedClusterBulkDelete>
  );
};
export default ClusterList;
